'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

type Role = 'staff' | 'client';
type AuthMode = 'login' | 'forgot';

interface StaffLoginData {
  email: string;
  password: string;
  remember: boolean;
}

interface ClientLoginData {
  clientCode: string;
  birthday: string;
}

interface ForgotData {
  email: string;
}

export default function SignUpLoginContent() {
  const [role, setRole] = useState<Role>('staff');
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const supabase = createClient();

  const staffForm = useForm<StaffLoginData>({ defaultValues: { remember: false } });
  const clientForm = useForm<ClientLoginData>();
  const forgotForm = useForm<ForgotData>();

  const handleStaffLogin = async (data: StaffLoginData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });
      if (error) {
        toast.error(error.message || 'Invalid email or password');
        return;
      }
      toast.success('Signed in successfully');
      window.location.href = '/receipt-management';
    } catch {
      toast.error('Sign in failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClientLogin = async (data: ClientLoginData) => {
    setIsSubmitting(true);
    try {
      // Look up client portal credentials by client_code + birthday
      const { data: cred, error } = await supabase
        .from('client_portal_credentials')
        .select('*, clients(*)')
        .eq('client_code', data.clientCode.trim().toUpperCase())
        .eq('birthday', data.birthday.trim())
        .eq('is_active', true)
        .maybeSingle();

      if (error || !cred) {
        toast.error('Invalid client code or birthday. Please contact the clinic.');
        return;
      }

      // Store client session in localStorage for the portal
      if (typeof window !== 'undefined') {
        localStorage.setItem('client_session', JSON.stringify({
          clientId: cred.clients?.id,
          clientCode: cred.client_code,
          clientName: cred.clients?.name,
          clientDob: cred.clients?.dob,
        }));
      }

      toast.success('Welcome to your client portal');
      // Also set a cookie so the middleware can detect the client session
      document.cookie = `client_session=1; path=/; max-age=${60 * 60 * 24 * 7}; SameSite=Lax`;
      window.location.href = '/client-dashboard';
    } catch {
      toast.error('Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgot = async (data: ForgotData) => {
    setIsSubmitting(true);
    try {
      await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });
      setForgotSent(true);
    } catch {
      toast.error('Failed to send reset link. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    { icon: 'DocumentCheckIcon', text: 'Every visit, recorded and summarized' },
    { icon: 'CalendarDaysIcon', text: 'Next appointments, suggested automatically' },
    { icon: 'EyeIcon', text: 'A clean summary your clients will love' },
    { icon: 'ShieldCheckIcon', text: 'Built with privacy in mind' },
  ];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left panel — branding (hidden on mobile) */}
      <div className="hidden lg:flex flex-col w-[480px] xl:w-[520px] flex-shrink-0 bg-primary px-12 py-10 text-primary-foreground relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-primary-foreground/5 -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-primary-foreground/5 translate-y-1/3 -translate-x-1/3" />

        {/* Logo */}
        <div className="flex items-center gap-3 mb-14 relative z-10">
          <div className="w-10 h-10 rounded-xl bg-primary-foreground/20 flex items-center justify-center">
            <Icon name="HeartIcon" size={22} className="text-primary-foreground" />
          </div>
          <span className="text-xl font-bold tracking-tight">Love Skin Clinic</span>
        </div>

        {/* Headline */}
        <div className="flex-1 relative z-10">
          <h1 className="text-3xl font-bold leading-tight mb-4">
            Your skin, beautifully documented
          </h1>
          <p className="text-primary-foreground/80 text-base leading-relaxed mb-10">
            Track treatments, celebrate progress, and never miss a next step — all in one place.
          </p>

          {/* Features */}
          <ul className="space-y-4">
            {features.map((f) => (
              <li key={`feature-${f.icon}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary-foreground/15 flex items-center justify-center flex-shrink-0">
                  <Icon name={f.icon as any} size={16} className="text-primary-foreground" />
                </div>
                <span className="text-sm text-primary-foreground/90">{f.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 pt-8 border-t border-primary-foreground/20 relative z-10">
          {[
            { value: '2,400+', label: 'Receipts issued' },
            { value: '98%', label: 'Accuracy rate' },
            { value: '4 min', label: 'Avg. time saved' },
          ].map((s) => (
            <div key={`stat-${s.label}`}>
              <p className="text-xl font-bold tabular-nums">{s.value}</p>
              <p className="text-xs text-primary-foreground/70 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-start lg:items-center justify-center px-4 sm:px-6 py-8 sm:py-10 bg-background min-h-screen lg:min-h-0">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <AppLogo size={44} />
            <span className="font-bold text-base text-foreground">Love Skin Clinic</span>
          </div>

          {/* Role toggle */}
          {mode !== 'forgot' && (
            <div className="flex gap-1 bg-muted rounded-xl p-1 mb-6 sm:mb-8">
              {(['staff', 'client'] as Role[]).map((r) => (
                <button
                  key={`role-${r}`}
                  onClick={() => setRole(r)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 ${
                    role === r ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={r === 'staff' ? 'UserCircleIcon' : 'IdentificationIcon'} size={16} />
                  {r === 'staff' ? 'Clinic Staff' : 'Client'}
                </button>
              ))}
            </div>
          )}

          {/* Forgot password */}
          {mode === 'forgot' && (
            <div>
              <button
                onClick={() => { setMode('login'); setForgotSent(false); }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
              >
                <Icon name="ArrowLeftIcon" size={14} />
                Back to sign in
              </button>
              <h2 className="text-2xl font-bold text-foreground mb-1">Reset your password</h2>
              <p className="text-sm text-muted-foreground mb-6 sm:mb-8">Enter your email and we&apos;ll send a reset link.</p>

              {forgotSent ? (
                <div className="flex flex-col items-center text-center py-8">
                  <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-4">
                    <Icon name="EnvelopeIcon" size={28} className="text-accent" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Check your inbox</h3>
                  <p className="text-sm text-muted-foreground">A password reset link has been sent to your email address.</p>
                </div>
              ) : (
                <form onSubmit={forgotForm.handleSubmit(handleForgot)} className="space-y-4">
                  <div>
                    <label className="label-text" htmlFor="forgotEmail">Email Address</label>
                    <input
                      id="forgotEmail"
                      type="email"
                      {...forgotForm.register('email', { required: 'Email is required' })}
                      className="input-field"
                      placeholder="you@loveskinclinic.com"
                    />
                    {forgotForm.formState.errors.email && (
                      <p className="text-danger text-xs mt-1">{forgotForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full text-sm flex items-center justify-center gap-2 py-3"
                  >
                    {isSubmitting ? <><Icon name="ArrowPathIcon" size={15} className="animate-spin" />Sending...</> : 'Send Reset Link'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Login — Staff */}
          {mode === 'login' && role === 'staff' && (
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
              <p className="text-sm text-muted-foreground mb-6 sm:mb-8">Sign in to your clinic staff account</p>

              <form onSubmit={staffForm.handleSubmit(handleStaffLogin)} className="space-y-4">
                <div>
                  <label className="label-text" htmlFor="staffEmail">Work Email</label>
                  <input
                    id="staffEmail"
                    type="email"
                    {...staffForm.register('email', { required: 'Email is required' })}
                    className="input-field"
                    placeholder="sarah@loveskinclinic.com"
                    autoComplete="email"
                  />
                  {staffForm.formState.errors.email && (
                    <p className="text-danger text-xs mt-1">{staffForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="label-text mb-0" htmlFor="staffPassword">Password</label>
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      id="staffPassword"
                      type={showPassword ? 'text' : 'password'}
                      {...staffForm.register('password', { required: 'Password is required' })}
                      className="input-field pr-10"
                      placeholder="Your password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {staffForm.formState.errors.password && (
                    <p className="text-danger text-xs mt-1">{staffForm.formState.errors.password.message}</p>
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" {...staffForm.register('remember')} className="rounded w-4 h-4" />
                  <span className="text-sm text-muted-foreground">Remember me for 30 days</span>
                </label>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full text-sm flex items-center justify-center gap-2 py-3"
                >
                  {isSubmitting ? (
                    <><Icon name="ArrowPathIcon" size={15} className="animate-spin" />Signing in...</>
                  ) : (
                    'Sign In to Dashboard'
                  )}
                </button>
              </form>

              <div className="mt-6 p-4 bg-muted/40 rounded-xl border border-border">
                <p className="text-xs text-muted-foreground flex items-start gap-2">
                  <Icon name="InformationCircleIcon" size={14} className="text-primary flex-shrink-0 mt-0.5" />
                  Staff accounts are created by the clinic administrator. Contact your admin if you need access.
                </p>
              </div>
            </div>
          )}

          {/* Login — Client (client code + birthday) */}
          {mode === 'login' && role === 'client' && (
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-1">Access Client Portal</h2>
              <p className="text-sm text-muted-foreground mb-6 sm:mb-8">
                Sign in with your client code and date of birth provided by the clinic.
              </p>

              <form onSubmit={clientForm.handleSubmit(handleClientLogin)} className="space-y-4">
                <div>
                  <label className="label-text" htmlFor="clientCode">Client Code</label>
                  <input
                    id="clientCode"
                    type="text"
                    {...clientForm.register('clientCode', { required: 'Client code is required' })}
                    className="input-field font-mono uppercase"
                    placeholder="e.g. CI-1042"
                    autoComplete="off"
                  />
                  {clientForm.formState.errors.clientCode && (
                    <p className="text-danger text-xs mt-1">{clientForm.formState.errors.clientCode.message}</p>
                  )}
                </div>
                <div>
                  <label className="label-text" htmlFor="clientBirthday">Date of Birth</label>
                  <input
                    id="clientBirthday"
                    type="text"
                    {...clientForm.register('birthday', { required: 'Date of birth is required' })}
                    className="input-field"
                    placeholder="MM/DD/YYYY"
                    autoComplete="bday"
                  />
                  {clientForm.formState.errors.birthday && (
                    <p className="text-danger text-xs mt-1">{clientForm.formState.errors.birthday.message}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full text-sm flex items-center justify-center gap-2 py-3"
                >
                  {isSubmitting ? (
                    <><Icon name="ArrowPathIcon" size={15} className="animate-spin" />Signing in...</>
                  ) : (
                    <>
                      <Icon name="ArrowRightCircleIcon" size={15} />
                      Access Client Portal
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 p-4 bg-muted/40 rounded-xl border border-border">
                <p className="text-xs text-muted-foreground flex items-start gap-2">
                  <Icon name="ShieldCheckIcon" size={14} className="text-accent flex-shrink-0 mt-0.5" />
                  Your client code and date of birth are set up by clinic staff when your profile is created. Contact the clinic if you have not received your credentials.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}